/**
 * Hooks React Query du blog réflexif (admin) : liste, détail, génération,
 * mise à jour, couverture. Lectures via le client RLS (admin) ; écritures via
 * les edge fns service_role.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth, isAdmin } from '@/lib/auth';
import {
  BLOG_PAGE_SIZE,
  blogSearchPattern,
  type BlogArticle,
  type BlogArticleSummary,
  type BlogBlockMetrics,
  type BlogEvaluation,
  type BlogBlockVersion,
  type NewsletterSubscriberSummary,
} from '@jeraisonne/db';
import {
  generateBlogArticle,
  generateBlogCover,
  generateBlogPoll,
  regenerateBlogArticle,
  updateBlogArticle,
  refineBlogBlock,
  switchBlogBlockVersion,
  deleteBlogBlockVersion,
  submitBlogForReview,
  moderateBlogArticle,
  type BlogPatch,
  type GenerateBlogInput,
  type GenerateBlogPollInput,
  type RefineBlogBlockInput,
} from './blog-client';

export const blogArticlesQueryKey = ['admin', 'blog-articles'] as const;
export const blogArticleQueryKey = (id: string) => ['admin', 'blog-article', id] as const;
export const blogEvaluationQueryKey = (id: string) => ['admin', 'blog-evaluation', id] as const;
export const blogEvaluationsQueryKey = (id: string) => ['admin', 'blog-evaluations', id] as const;
export const blockVersionsQueryKey = (id: string) => ['admin', 'blog-block-versions', id] as const;

const SUMMARY_COLS =
  'id, slug, title, subtitle, status, visibility, illustration_url, reading_time, view_count, seo, target_roles, created_at, published_at, generation_status';

/** Page de gestion (liste paginée + recherche). */
export interface AdminBlogPage {
  articles: BlogArticleSummary[];
  total: number;
}

/**
 * Liste de gestion du blog. Un ADMIN voit tous les articles ; un TUTEUR ne voit
 * que les SIENS (`author_id`) — la RLS l'y limiterait de toute façon, mais on
 * filtre explicitement pour ne pas mélanger les articles publics d'autrui dans
 * sa liste d'édition. Recherche (`q` → `search_text` ilike, migration 0139) +
 * pagination (`range` + `count: 'exact'`, 10/page).
 */
export function useAdminBlogArticles(opts: { q?: string; page?: number } = {}) {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const userId = user?.id ?? null;
  const search = (opts.q ?? '').trim();
  const page = Math.max(1, Math.floor(opts.page ?? 1));
  const from = (page - 1) * BLOG_PAGE_SIZE;
  const to = from + BLOG_PAGE_SIZE - 1;

  return useQuery({
    queryKey: [...blogArticlesQueryKey, admin ? 'all' : userId, search, page] as const,
    queryFn: async (): Promise<AdminBlogPage> => {
      let q = supabase
        .from('blog_articles')
        .select(SUMMARY_COLS, { count: 'exact' })
        .order('created_at', { ascending: false });
      if (!admin && userId) q = q.eq('author_id', userId);
      const pattern = blogSearchPattern(search);
      if (pattern) q = q.ilike('search_text', pattern);
      const { data, error, count } = await q.range(from, to);
      if (error) throw new Error(error.message);
      return { articles: (data ?? []) as unknown as BlogArticleSummary[], total: count ?? 0 };
    },
    enabled: admin || !!userId,
    // Garde la page précédente affichée pendant le chargement de la suivante.
    placeholderData: keepPreviousData,
    // Rafraîchit tant qu'un article de la page est en cours de génération.
    refetchInterval: (query) =>
      (query.state.data?.articles ?? []).some((a) => a.generation_status === 'generating')
        ? 4000
        : false,
  });
}

export function useAdminBlogArticle(id: string) {
  return useQuery({
    queryKey: blogArticleQueryKey(id),
    queryFn: async (): Promise<BlogArticle | null> => {
      const { data, error } = await supabase
        .from('blog_articles')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as unknown as BlogArticle) ?? null;
    },
    enabled: !!id,
    // Génération asynchrone : tant que c'est « generating », on poll.
    refetchInterval: (query) =>
      query.state.data?.generation_status === 'generating' ? 3000 : false,
  });
}

export function useAdminBlogMetrics(id: string) {
  return useQuery({
    queryKey: ['admin', 'blog-metrics', id] as const,
    queryFn: async (): Promise<BlogBlockMetrics[]> => {
      const { data, error } = await supabase
        .from('blog_block_metrics')
        .select('*')
        .eq('article_id', id)
        .order('block_index', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as BlogBlockMetrics[];
    },
    enabled: !!id,
  });
}

/**
 * Vote brut d'un sondage joint à son abonné newsletter (lecture admin directe :
 * `blog_poll_votes` + `newsletter_subscribers` ont chacune une policy
 * `admin_read`). Alimente la page de consultation (distributions + nb de
 * répondants) et la page newsletter filtrée sur les répondants d'un article.
 */
export interface ArticlePollVoteRow {
  block_index: number;
  choice_index: number;
  subscriber_id: string | null;
  subscriber: NewsletterSubscriberSummary | null;
}

export function useArticlePollVotes(id: string) {
  return useQuery({
    queryKey: ['admin', 'blog-poll-votes', id] as const,
    queryFn: async (): Promise<ArticlePollVoteRow[]> => {
      const { data, error } = await supabase
        .from('blog_poll_votes')
        .select(
          'block_index, choice_index, subscriber_id, ' +
            'newsletter_subscribers(id, email, status, source, confirmed_at, created_at)',
        )
        .eq('article_id', id)
        .order('created_at', { ascending: true });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as unknown as Array<{
        block_index: number;
        choice_index: number;
        subscriber_id: string | null;
        newsletter_subscribers: NewsletterSubscriberSummary | null;
      }>;
      return rows.map((r) => ({
        block_index: r.block_index,
        choice_index: r.choice_index,
        subscriber_id: r.subscriber_id,
        subscriber: r.newsletter_subscribers ?? null,
      }));
    },
    enabled: !!id,
  });
}

export function useGenerateBlogArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateBlogInput) => generateBlogArticle(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: blogArticlesQueryKey });
    },
  });
}

export function useUpdateBlogArticle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: BlogPatch) => updateBlogArticle(id, patch),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogArticleQueryKey(id) });
      void qc.invalidateQueries({ queryKey: blogArticlesQueryKey });
    },
  });
}

export function useRegenerateBlogArticle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => regenerateBlogArticle(id),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogArticleQueryKey(id) });
    },
  });
}

/**
 * (Re)génère le sondage d'un bloc. Ne touche pas au cache : le sondage retourné
 * est fusionné dans le brouillon local par l'appelant (modifications non encore
 * enregistrées préservées).
 */
export function useGenerateBlogPoll(id: string) {
  return useMutation({
    mutationFn: (input: GenerateBlogPollInput) => generateBlogPoll(id, input),
  });
}

// ─── Raffinage manuel itératif : évaluations + versions par bloc ─────────────

/** Dernière évaluation « juge » de l'article (itération la plus haute). */
export function useLatestBlogEvaluation(id: string) {
  return useQuery({
    queryKey: blogEvaluationQueryKey(id),
    queryFn: async (): Promise<BlogEvaluation | null> => {
      const { data, error } = await supabase
        .from('blog_article_evaluations')
        .select('*')
        .eq('article_id', id)
        .order('iteration', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as unknown as BlogEvaluation) ?? null;
    },
    enabled: !!id,
  });
}

/** Historique des évaluations (progression du score au fil des itérations). */
export function useBlogEvaluations(id: string) {
  return useQuery({
    queryKey: blogEvaluationsQueryKey(id),
    queryFn: async (): Promise<BlogEvaluation[]> => {
      const { data, error } = await supabase
        .from('blog_article_evaluations')
        .select('*')
        .eq('article_id', id)
        .order('iteration', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as BlogEvaluation[];
    },
    enabled: !!id,
  });
}

/** Versions conservées de chaque bloc (triées slot puis n° de version). */
export function useBlockVersions(id: string) {
  return useQuery({
    queryKey: blockVersionsQueryKey(id),
    queryFn: async (): Promise<BlogBlockVersion[]> => {
      const { data, error } = await supabase
        .from('blog_block_versions')
        .select('*')
        .eq('article_id', id)
        .order('block_index', { ascending: true })
        .order('version_no', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as BlogBlockVersion[];
    },
    enabled: !!id,
  });
}

/**
 * Régénère un bloc (versions conservées). Le composant re-hydrate son brouillon
 * depuis l'article renvoyé ; on invalide aussi les versions.
 */
export function useRefineBlogBlock(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RefineBlogBlockInput) => refineBlogBlock(id, input),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogArticleQueryKey(id) });
      void qc.invalidateQueries({ queryKey: blockVersionsQueryKey(id) });
    },
  });
}

/** Bascule la version active d'un bloc (renvoie l'article à jour). */
export function useSwitchBlockVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => switchBlogBlockVersion(versionId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogArticleQueryKey(id) });
      void qc.invalidateQueries({ queryKey: blockVersionsQueryKey(id) });
    },
  });
}

/** Supprime une version de bloc (refuse l'active / la dernière). */
export function useDeleteBlockVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => deleteBlogBlockVersion(versionId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blockVersionsQueryKey(id) });
    },
  });
}

export function useGenerateBlogCover(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { prompt: string; title?: string }) =>
      generateBlogCover(id, args.prompt, args.title),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: blogArticleQueryKey(id) });
    },
  });
}

// ─── Modération : soumission (tuteur) + décision (admin) ─────────────────────

export const pendingBlogArticlesQueryKey = ['admin', 'blog-pending-review'] as const;

/** Article en attente de validation (liste de modération admin). */
export interface PendingBlogArticle {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  reading_time: number;
  created_at: string;
  author: { display_name: string | null } | null;
}

/** Articles `pending_review` à modérer (admin only — la RLS admin_all les expose). */
export function usePendingReviewBlogArticles(enabled: boolean) {
  return useQuery({
    queryKey: pendingBlogArticlesQueryKey,
    queryFn: async (): Promise<PendingBlogArticle[]> => {
      const { data, error } = await supabase
        .from('blog_articles')
        .select(
          'id, slug, title, subtitle, reading_time, created_at, ' +
            'author:profiles!blog_articles_author_id_fkey(display_name:visible_name)',
        )
        .eq('visibility', 'pending_review')
        .order('updated_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as PendingBlogArticle[];
    },
    enabled,
  });
}

function invalidateBlogState(qc: ReturnType<typeof useQueryClient>, id: string) {
  void qc.invalidateQueries({ queryKey: blogArticleQueryKey(id) });
  void qc.invalidateQueries({ queryKey: blogArticlesQueryKey });
  void qc.invalidateQueries({ queryKey: pendingBlogArticlesQueryKey });
}

/** Tuteur/admin : soumet le brouillon à la validation admin (→ pending_review). */
export function useSubmitBlogForReview(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => submitBlogForReview(id, 'submit'),
    onSettled: () => invalidateBlogState(qc, id),
  });
}

/** Tuteur/admin : retire un article en attente (→ private). */
export function useWithdrawBlogReview(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => submitBlogForReview(id, 'withdraw'),
    onSettled: () => invalidateBlogState(qc, id),
  });
}

/** Admin : approuve (→ public+published) ou refuse (→ private) un article soumis. */
export function useModerateBlogArticle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { action: 'approve' | 'reject'; reason?: string }) =>
      moderateBlogArticle(id, args.action, args.reason),
    onSettled: () => invalidateBlogState(qc, id),
  });
}
